/**
 * Pure, dependency-free date/status logic for Campañas. Isolated from
 * `campanias.ts` (which pulls in the SharePoint client) so it can be
 * unit-tested in isolation — see `campaniasLogic.test.ts`.
 */

export const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'yyyy-mm-dd' (HTML date input) → 'dd/mm/yyyy' (SharePoint storage). */
export function isoToAr(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

/** 'dd/mm/yyyy' → 'yyyy-mm-dd' for prefilling HTML date inputs. */
export function arToIso(ar: string | null | undefined): string {
  if (!ar) return '';
  const [d, m, y] = ar.split('/');
  if (!y || !m || !d) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Local 'yyyy-mm-dd' today (browser timezone). */
export function todayIsoLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Whether a campaign is currently vigente by its date window
 * (`Desde_C <= today <= Hasta_C`). Status ('Activo') is filtered
 * separately via `listActiveCampanias`; this adds the date check that
 * neither the list query nor the nightly expiry cron cover intraday —
 * without it a not-yet-started or just-expired campaign would still apply
 * in cotizaciones. Missing/unparseable bounds are treated as open so a
 * malformed date can't hide an otherwise-active campaign. The server
 * (`campaignValidation.ts`) enforces the same window authoritatively.
 */
export function isCampaniaVigente(
  fields: { Desde_C?: string | null; Hasta_C?: string | null },
  today: string = todayIsoLocal(),
): boolean {
  const desde = arToIso(fields.Desde_C);
  const hasta = arToIso(fields.Hasta_C);
  if (desde && desde > today) return false;
  if (hasta && hasta < today) return false;
  return true;
}

/**
 * Three-state status for display, derived from the stored status + the
 * `Hasta_C` date. The backend can only persist `Activo`/`Inactivo` (the
 * `abm_status` enum maps `Eliminado → 'Inactivo'`), so 'Finalizado' is
 * never stored — we recover it here:
 *   - past `Hasta_C` (regardless of stored status) → 'Finalizado'
 *   - else stored 'Activo' → 'Activo'
 *   - else → 'Inactivo' (manually deactivated, still within its window)
 * This distinguishes an expired campaign from one deleted before expiry,
 * and makes the otherwise-dead 'Finalizado' filter meaningful.
 */
export function campaniaDisplayStatus(
  fields: { Status_C?: string | null; Hasta_C?: string | null },
  today: string = todayIsoLocal(),
): 'Activo' | 'Finalizado' | 'Inactivo' {
  const hasta = arToIso(fields.Hasta_C);
  if (hasta && hasta < today) return 'Finalizado';
  return (fields.Status_C ?? '') === 'Activo' ? 'Activo' : 'Inactivo';
}
